import {
  DollarOutlined,
  MenuOutlined,
  SettingOutlined,
  ToolOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { css } from '@linaria/core';
import { Link, useLocation } from '@tanstack/react-router';
import { Button, Grid, Layout, Menu } from 'antd';
import { ReactNode, useState } from 'react';

import { getAdminRoutes } from '@modules/index';

import AdminNavigationDrawer from './components/navigation-drawer';

const ADMIN_SECTIONS = {
  operation: { label: 'Operação', order: 10, icon: <ToolOutlined /> },
  catalog: { label: 'Catálogo', order: 20, icon: <UnorderedListOutlined /> },
  finance: {
    label: 'Pedidos e financeiro',
    order: 30,
    icon: <DollarOutlined />,
  },
  platform: { label: 'Plataforma', order: 40, icon: <SettingOutlined /> },
} as const;

const styles = {
  layout: css`
    min-width: 0;
    flex: 1;
    background: transparent;
  `,
  sider: css`
    position: sticky !important;
    top: 0;
    height: 100dvh;
    overflow-y: auto;
    border-inline-end: 1px solid var(--ant-color-border-secondary);
    background: var(--ant-color-bg-container) !important;

    .ant-layout-sider-trigger {
      background-color: var(--ant-color-bg);
      color: var(--ant-color-primary);
    }
  `,
  mobileBar: css`
    position: sticky;
    top: 0;
    z-index: 10;
    padding: var(--ant-padding-sm) var(--ant-padding);
    border-bottom: 1px solid var(--ant-color-border-secondary);
    background: var(--ant-color-bg-container);
  `,
  content: css`
    min-width: 0;
  `,
};

interface AdminLayoutProps {
  children: ReactNode;
}

const adminRoutes = getAdminRoutes()
  .flatMap((route) => route.children ?? [route])
  .filter(
    (route) =>
      route.path.startsWith('/administracao') &&
      !route.hidden &&
      route.adminSection,
  );

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const { md } = Grid.useBreakpoint();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const selectedRoute = [...adminRoutes]
    .sort((first, second) => second.path.length - first.path.length)
    .find((route) => location.pathname.startsWith(route.path));
  const items = Object.entries(ADMIN_SECTIONS)
    .sort(([, first], [, second]) => first.order - second.order)
    .map(([section, definition]) => ({
      key: section,
      label: definition.label,
      icon: definition.icon,
      children: adminRoutes
        .filter((route) => route.adminSection === section)
        .sort(
          (first, second) => (first.adminOrder ?? 0) - (second.adminOrder ?? 0),
        )
        .map((route) => ({
          key: route.path,
          icon: route.icon ? <route.icon /> : undefined,
          label: (
            <Link to={route.path} onClick={() => setDrawerOpen(false)}>
              {route.title}
            </Link>
          ),
        })),
    }))
    .filter(({ children: sectionChildren }) => sectionChildren.length > 0);
  const menu = (
    <Menu
      mode="inline"
      items={items}
      selectedKeys={selectedRoute ? [selectedRoute.path] : []}
      defaultOpenKeys={[Object.keys(ADMIN_SECTIONS)[0]]}
    />
  );

  return (
    <Layout className={styles.layout}>
      {md ? (
        <Layout.Sider className={styles.sider} width={248} collapsible>
          {menu}
        </Layout.Sider>
      ) : (
        <div className={styles.mobileBar}>
          <Button icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)}>
            Administração
          </Button>
          <AdminNavigationDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
          >
            {menu}
          </AdminNavigationDrawer>
        </div>
      )}
      <Layout.Content className={styles.content}>{children}</Layout.Content>
    </Layout>
  );
};

export default AdminLayout;
